import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import clsx from 'clsx'
import React from 'react'

import { formatLengthValue } from '@/utils/css'

import Styles from './Text.module.scss'

const textVariants = cva(Styles.textBase, {
  variants: {
    variant: {
      'nextTitle-1': Styles.nextTitle1,
      'nextTitle-2': Styles.nextTitle2,
      'nextTitle-3': Styles.nextTitle3,
      'nextTitle-4': Styles.nextTitle4,
      'title-1': Styles.title1,
      'title-2': Styles.title2,
      'title-3': Styles.title3,
      nextSubtitle: Styles.nextSubtitle,
      subtitle: Styles.subtitle,
      nextBody: Styles.nextBody,
      body: Styles.body,
      nextCaption: Styles.nextCaption,
      caption: Styles.caption,
      overline: Styles.overline,
      nextOverline: Styles.nextOverline,
      serial: Styles.serial,
      tinyCaption: Styles.tinyCaption,
      'header-2': Styles.header2,
      'heading-04': Styles.heading04,
      'heading-05': Styles.heading05
    },
    weight: {
      regular: Styles.regular,
      semiBold: Styles.semiBold,
      bold: Styles.bold,
      extraBold: Styles.extraBold
    },
    align: {
      start: Styles.start,
      end: Styles.end,
      center: Styles.center
    }
  },
  compoundVariants: [
    {
      variant: 'nextSubtitle',
      weight: 'bold',
      className: Styles.extraBold
    },
    {
      variant: 'nextBody',
      weight: ['bold', 'semiBold'],
      className: Styles.nextBodyBold
    },
    {
      variant: ['caption', 'overline'],
      weight: 'bold',
      className: Styles.semiBold
    }
  ],
  defaultVariants: {
    variant: 'nextBody'
  }
})

export interface ITextProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof textVariants> {
  asChild?: boolean
  align?: 'center' | 'start' | 'end'
  gutter?: number | string
}

const Text = React.forwardRef<HTMLParagraphElement, ITextProps>(
  (
    {
      className,
      variant,
      weight,
      gutter,
      asChild = false,
      align = 'start',
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : 'p'

    return (
      <Component
        style={
          (gutter
            ? {
                '--text-gutter': formatLengthValue(gutter)
              }
            : {}) as React.CSSProperties
        }
        className={clsx(textVariants({ variant, weight, align, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Text.displayName = 'Text'

export { textVariants }
export default Text
